import React from "react";
import _ from "lodash";
import TaxonomyData from "./taxonomyData.json";
import { hardColors } from "./lib/constants";

const getBaseUrl = () => (process.env.NODE_ENV === "development" ? "http://localhost:8080" : "");

export const getGroups = (entries) => {
  const n_nearest = 5;
  const extent = 4;
  let clusters = [];
  if (_.size(entries) !== 0) {
    const positions = entries[0];
    const keyphrases = entries[1];
    _.forEach(keyphrases, (keyphrase, index) => {
      const closest = _.reject(
        findClosestPoints(positions[index], positions, keyphrases, n_nearest),
        (point) => point.distance > extent
      );
      const clusterIndexes = _.map(
        _.keys(
          _.pickBy(clusters, (cluster) =>
            _.some(_.union(closest, [{ tag: keyphrase }]), (data) => _.includes(cluster, data.tag))
          )
        ),
        Number
      );
      const tags = _.union(
        _.map(closest, (x) => x.tag),
        [keyphrase]
      );
      if (!_.isEmpty(clusterIndexes)) {
        const combinedClusters = _.union(..._.map(clusterIndexes, (index) => clusters[index]), tags);
        clusters[clusterIndexes[0]] = combinedClusters;
        _.forEach(_.slice(clusterIndexes, 1), (index) => _.remove(clusters, (v, i) => i === index));
      } else {
        clusters = _.union(clusters, [tags]);
      }
    });
    return [clusters];
  } else {
    return [];
  }
};

export const getTaxonomyGroups = (positions, tags) => {
  const n_nearest = 4;
  const extent = 7.5;
  const clusters = {};
  if (_.size(positions) === _.size(tags) && !_.isEmpty(positions)) {
    const mergedTags = {};
    const mergedPositions = {};
    const taxonomyTagIndexes = [];
    const taxonomyTagList = _.union(..._.map(TaxonomyData, (taxonomyTags) => taxonomyTags));
    const keyphrases = _.reject(tags, (tag) => _.includes(taxonomyTagList, tag));
    const keyphrasePositions = _.map(
      keyphrases,
      (keyphrase) => positions[_.findIndex(tags, (tag) => tag === keyphrase)]
    );
    const adjacencyMatrices = _.map(taxonomyTagList, (taxonomyTag) => {
      const taxonomyTagIndex = _.findIndex(tags, (tag) => tag === taxonomyTag);
      taxonomyTagIndexes[taxonomyTagIndex] = taxonomyTag;
      mergedPositions[taxonomyTag] = _.union([positions[taxonomyTagIndex]], keyphrasePositions);
      mergedTags[taxonomyTag] = _.union([taxonomyTag], keyphrases);
      return _.map(mergedPositions[taxonomyTag], (p1) => {
        const closest = _.reject(
          findClosestPoints(p1, mergedPositions[taxonomyTag], mergedTags[taxonomyTag], n_nearest),
          (point) => point.distance > extent
        );
        const adjacencyRow = new Array(_.size(mergedTags[taxonomyTag]));
        _.forEach(closest, (point) => (adjacencyRow[point.index] = point.distance));
        return adjacencyRow;
      });
    });
    const dijkstras = _.map(adjacencyMatrices, (adjacencyMatrix) => dijkstra(adjacencyMatrix));
    const trees = _.map(dijkstras, (dijkstra, index) => {
      const taxonomyTag = taxonomyTagIndexes[index];
      const lastIndex = _.size(dijkstra.p) - 1;
      const tree = {
        treeGroup: _.keys(_.pickBy(TaxonomyData, (data) => _.some(data, (tag) => tag === taxonomyTag)))[0],
        tree: {}
      };
      _.forEach(dijkstra.v, (node, index) => {
        if (index === 0) {
          tree.tree[taxonomyTag] = {
            children: [],
            position: positions[_.findIndex(tags, (tag) => tag === taxonomyTag)]
          };
        } else {
          const parent = dijkstra.p[lastIndex][node - 2];
          tree.tree[keyphrases[node - 2]] = {
            children: [],
            parent: parent === 1 ? taxonomyTag : keyphrases[parent - 2],
            position: keyphrasePositions[node - 2]
          };
          tree.tree[parent === 1 ? taxonomyTag : keyphrases[parent - 2]].children.push(keyphrases[node - 2]);
        }
      });
      return tree;
    });
    _.forEach(keyphrases, (keyphrase, index) => {
      let treesInclude = [];
      _.forEach(trees, (tree) => {
        if (tree.tree[keyphrase] !== undefined) {
          treesInclude = _.union(treesInclude, [tree]);
        }
      });
      if (!_.isEmpty(treesInclude)) {
        const clusterEntry = {
          node: keyphrase,
          parents: _.union(..._.map(treesInclude, (treeInclude) => [treeInclude.tree[keyphrase].parent])),
          children: _.union(..._.map(treesInclude, (treeInclude) => treeInclude.tree[keyphrase].children)),
          position: keyphrasePositions[index]
        };
        if (clusters[treesInclude[0].treeGroup] === undefined) {
          clusters[treesInclude[0].treeGroup] = [];
        }
        clusters[treesInclude[0].treeGroup].push(clusterEntry);
      } else {
        const clusterEntry = {
          node: keyphrase,
          parents: [],
          children: [],
          position: keyphrasePositions[index]
        };
        if (clusters["noGroup"] === undefined) {
          clusters["noGroup"] = [];
        }
        clusters["noGroup"].push(clusterEntry);
      }
    });
    _.forEach(trees, (tree) => {
      const treeGroup = tree.treeGroup;
      const key = Object.keys(tree.tree)[0];
      const clusterEntry = tree.tree[key];
      if (clusters[treeGroup] === undefined) {
        clusters[treeGroup] = [];
      }
      clusters[treeGroup].push({
        node: key,
        parents: [],
        children: clusterEntry.children,
        position: clusterEntry.position
      });
    });

    _.forEach(keyphrases, (keyphrase, index) => {
      const clustersIncludes = _.filter(clusters, (cluster) => _.some(cluster, (node) => node.node === keyphrase));
      if (_.size(clustersIncludes) > 1) {
        throw new Error("yes");
      }
    });
    return [clusters];
  } else {
    throw new Error("positions and keyphrases must be arrays with same length that aren't empty!");
  }
};

export const dijkstra = (adjacencyMatrix) => {
  const arrSize = _.size(adjacencyMatrix) - 1;
  const d = []; // Saves distance from startNode
  const p = []; // Saves parent node
  const v = []; // Saves order of nodes
  const connected = new Array(arrSize + 1);
  _.fill(connected, false);
  let currentNode = 1;
  for (let i = 0; i < arrSize + 1; i++) {
    if (currentNode !== -1) {
      const weight = i !== 0 ? d[i - 1][currentNode - 2] : 0;
      let min = Number.MAX_SAFE_INTEGER;
      let minIndex = -1;
      connected[currentNode - 1] = true;
      d[i] = [];
      p[i] = [];
      for (let j = 0; j < arrSize; j++) {
        if (
          adjacencyMatrix[currentNode - 1][j + 1] !== undefined &&
          (i === 0 || d[i - 1][j] === undefined || weight + adjacencyMatrix[currentNode - 1][j + 1] < d[i - 1][j])
        ) {
          d[i][j] = weight + adjacencyMatrix[currentNode - 1][j + 1];
          p[i][j] = currentNode;
        } else if (i !== 0) {
          d[i][j] = d[i - 1][j];
          p[i][j] = p[i - 1][j];
        }
        if (i < arrSize && !connected[j + 1] && d[i][j] !== undefined && d[i][j] < min) {
          minIndex = j + 2;
          min = d[i][j];
        }
      }
      v[i] = currentNode;
      currentNode = minIndex;
    } else {
      break;
    }
  }
  return { v: v, d: d, p: p };
};

export const getDistance = (a, b) => {
  if (_.isArray(a) && _.isArray(b) && _.size(a) === _.size(b)) {
    const differences = _.map(a, (aPos, index) => aPos - b[index]);
    return Math.sqrt(_.sumBy(differences, (difference) => difference * difference));
  } else {
    throw new Error("a and b must be arrays with the same length that only contain numbers!");
  }
};

export const findClosestPoints = (a, positions, tags, n) => {
  if (_.isArray(a) && _.isArray(positions) && _.isArray(tags) && _.size(positions) === _.size(tags) && _.isInteger(n)) {
    const distances = _.map(positions, (position, index) => ({
      tag: tags[index],
      index: index,
      position: position,
      distance: getDistance(a, position)
    }));
    const closestDistances = _.slice(_.orderBy(distances, ["distance"], ["asc"]), 1, n + 1);
    return closestDistances;
  } else {
    throw new Error(
      "a, positions and tags must be arrays. positions and tags must be the same length. n must be an integer."
    );
  }
};

/**
 * @param {string} text
 * @param {string[]} relatedTexts
 * @returns {any}
 */
export const fetchRelations = async (text, relatedTexts) => {
  const relation_response = await fetch(getBaseUrl() + "/contantrelations", {
    mode: "cors",
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      text: text,
      relatedTexts: relatedTexts
    })
  });
  const response = await relation_response.json();
  return response;
};

export const fetchKeyphrases = async (sentences) => {
  const keyphrase_responses = _.map(sentences, (sentence) =>
    fetch(getBaseUrl() + "/constantkeyphrases", {
      mode: "cors",
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        sentence: sentence
      })
    })
  );
  const fetchedKeyphrases = await Promise.all(keyphrase_responses);
  const keyphrases = await Promise.all(_.map(fetchedKeyphrases, (keyphrases) => keyphrases.json()));
  const out = [];
  _.forEach(keyphrases, (keyphrase, index) => {
    out[index] = [sentences[index], keyphrase.keyphrases];
  });
  return {
    value: out
  };
};

export const getGroupColors = (n) => {
  return [hardColors];
};

export const keyPhraseExtraction = async (sentenceArr) => {
  const ATA_Response = await fetch(getBaseUrl() + "/textAnalytics", {
    mode: "cors",
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ sentences: sentenceArr })
  });
  const response = await ATA_Response.json();
  return response;
};

export const getUMAP = async (list) => {
  const keyphrases = [];
  _.forEach(list, (value) => {
    _.forEach(value[1], (keyphrase) => {
      keyphrases.push(keyphrase);
    });
  });
  const UMAP_Response = await fetch(getBaseUrl() + "/umap", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    mode: "cors",
    body: JSON.stringify({ keyphrases: keyphrases })
  });
  const response = await UMAP_Response.json();
  _.forEach(list, (value) => {
    value[2] = response.value.splice(0, value[1].length);
  });
  return list;
};

export const getUMAPfromKeyPhrases = async (list) => {
  const UMAP_Response = await fetch(getBaseUrl() + "/umap", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    mode: "cors",
    body: JSON.stringify({ keyphrases: list })
  });
  const response = await UMAP_Response.json();
  return response.value;
};

/**
 * @returns {{width: number, height: number}}
 */
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = React.useState({
    width: undefined,
    height: undefined
  });
  React.useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
};

/**
 * @returns {boolean}
 */
export const useMobile = () => {
  const windowSize = useWindowSize();
  return windowSize.width < 900;
};
